import { useEffect, useMemo, useState } from "react";
import { IconSearch, IconTrash } from "@tabler/icons-react";
import { Button, Card, Group, Input, PaginadorTabla, Stack, Text, useConfirmDialog } from "@/components/ui";
import { useCuentas } from "@/features/catalogos/hooks/use-catalogos";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { listarMapeosClaveCuenta, type MapeoClaveCuenta } from "../services/mapeos-clave-cuenta-service";
import { useMapeosClaveCuenta } from "../hooks/use-mapeos-clave-cuenta";
import { useEliminarMasivoMapeos } from "../hooks/use-mapeos-clave-cuenta-mutations";
import { MapeosHeader } from "./MapeosHeader";
import { MapeoCrearForm } from "./MapeoCrearForm";
import { MapeosTable } from "./MapeosTable";

const PAGE_SIZE = 15;

/**
 * Gestor del catálogo "clave producto/servicio SAT → cuenta contable"
 * (equivalente de dbo.Tag). Container thin · orquesta `modoCrear` y la
 * búsqueda + paginado SERVER-SIDE (query params con debounce · primera página
 * precargada por el loader del route).
 */
export function AdminMapeosClaveCuentaPage() {
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(1);
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);
  const [modoCrear, setModoCrear] = useState(false);

  const { data: cuentas } = useCuentas();

  const serverParams = useMemo(
    () => ({
      ...(busquedaDebounced.trim() ? { busqueda: busquedaDebounced.trim() } : {}),
      page,
      pageSize: PAGE_SIZE,
    }),
    [busquedaDebounced, page]
  );

  useEffect(() => {
    setPage(1);
  }, [busquedaDebounced]);

  const { data } = useMapeosClaveCuenta(serverParams);
  const mapeos = data?.items ?? [];
  const total = data?.total ?? 0;

  // Selección múltiple (persiste entre páginas · los ids son estables).
  const [seleccionadas, setSeleccionadas] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setSeleccionadas((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleTodasPagina = () =>
    setSeleccionadas((prev) => {
      const n = new Set(prev);
      const todas = mapeos.length > 0 && mapeos.every((m) => n.has(m.id));
      for (const m of mapeos) {
        if (todas) n.delete(m.id);
        else n.add(m.id);
      }
      return n;
    });

  const confirm = useConfirmDialog();
  const eliminar = useEliminarMasivoMapeos({ onEliminado: () => setSeleccionadas(new Set()) });
  const confirmarEliminar = async () => {
    const n = seleccionadas.size;
    const ok = await confirm({
      title: `Eliminar ${n} ${n === 1 ? "relación" : "relaciones"}`,
      message:
        "Se ELIMINARÁN de forma permanente las que no estén en uso. Las que ya derivaron una cuenta en algún prorrateo se DESACTIVARÁN (baja lógica) en vez de borrarse, para conservar la trazabilidad. Los borrados no se pueden deshacer.",
      confirmLabel: "Eliminar seleccionadas",
      confirmColor: "peligro",
      icon: <IconTrash size={14} />,
    });
    if (ok) eliminar.mutate([...seleccionadas]);
  };

  const obtenerTodas = async (): Promise<MapeoClaveCuenta[]> => {
    const b = busquedaDebounced.trim();
    const todas: MapeoClaveCuenta[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarMapeosClaveCuenta({ ...(b ? { busqueda: b } : {}), page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <MapeosHeader total={total} modoCrear={modoCrear} onIniciarCrear={() => setModoCrear(true)} />
      <Stack gap="md">
        {modoCrear ? <MapeoCrearForm cuentas={cuentas} onSalir={() => setModoCrear(false)} /> : null}
        <Input
          placeholder="Buscar por clave SAT, cuenta o nombre…"
          leftSection={<IconSearch size={14} stroke={1.7} />}
          value={busqueda}
          onChange={(e) => setBusqueda(e.currentTarget.value)}
          wrapperClassName="max-w-90"
        />
        {seleccionadas.size > 0 && (
          <Group
            justify="space-between"
            className="rounded-md border border-peligro-400/40 bg-peligro-600/5 px-3 py-2"
          >
            <Text size="sm" fw={500}>
              {seleccionadas.size} {seleccionadas.size === 1 ? "relación seleccionada" : "relaciones seleccionadas"}
            </Text>
            <Group gap="xs">
              <Button variant="subtle" size="xs" onClick={() => setSeleccionadas(new Set())}>
                Limpiar selección
              </Button>
              <Button
                size="xs"
                className="bg-peligro-600 hover:bg-peligro-700"
                leftSection={<IconTrash size={14} />}
                loading={eliminar.isPending}
                onClick={confirmarEliminar}
              >
                Eliminar seleccionadas
              </Button>
            </Group>
          </Group>
        )}
        <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
        <Card padding="lg">
          <MapeosTable
            mapeos={mapeos}
            cuentas={cuentas}
            obtenerFilasExport={obtenerTodas}
            seleccionadas={seleccionadas}
            onToggle={toggle}
            onToggleTodasPagina={toggleTodasPagina}
          />
        </Card>
        <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="mapeos" />
      </Stack>
    </div>
  );
}
